import { faMap, faMedal } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@vadiun/react-components';
import moment from 'moment';
import { Car, Clock, TrendingUp } from 'lucide-react';
import { mapDriverCategoryToIconColor } from '../models/ChoferCategoria';
import { ChoferEstado, driverStateTextClass } from '../models/ChoferEstado';
import { ChoferTrabajando } from '../models/ChoferTrabajando';
import useChoferes from '../services/ChoferService';
import { useEffect, useState } from 'react';
import { ChoferModelFromBack } from '../models/Chofer';

type Props = {
  driver: ChoferTrabajando;
  closeShift: (id: number) => void;
  forceBreak: (id: number) => void;
  extendExcess: (driver: ChoferTrabajando) => void;
  isOpened: boolean;
};

const cardClass =
  'rounded-2xl border border-secondary-200 bg-white p-6 dark:border-white/10 dark:bg-darkLight';
const labelClass =
  'text-xs font-medium uppercase tracking-wide text-inkMuted dark:text-inkMuted-dark';
const moneyClass = 'font-semibold text-green-600 dark:text-success-dark';

function CardHeader({
  icon,
  title
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <span className="text-secondary-500">{icon}</span>
      <h2 className="text-lg font-semibold text-ink dark:text-ink-dark">
        {title}
      </h2>
    </div>
  );
}

function InfoField({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className={labelClass}>{label}</p>
      <div className="mt-0.5 text-sm text-ink dark:text-ink-dark">
        {children}
      </div>
    </div>
  );
}

const vehicleTypeBadgeClass = (vehicleType?: string) => {
  switch (vehicleType) {
    case 'ejecutivo':
      return 'text-gray-600 dark:text-gray-300';
    case 'utilitario':
      return 'text-orange-500 dark:text-warning-dark';
    case 'vip':
      return 'text-yellow-500 dark:text-yellow-300';
    default:
      return 'text-blue-600 dark:text-info-dark';
  }
};

const vehicleTypeLabel = (vehicleType?: string) => {
  if (vehicleType === 'utilitario') return 'Plus';
  if (vehicleType === 'vip') return 'Preferencial';
  if (vehicleType === 'ejecutivo') return 'Ejecutivo';
  return vehicleType;
};

export function WorkingDriverInfo({
  driver,
  closeShift,
  forceBreak,
  isOpened,
  extendExcess
}: Props) {
  const ChoferService = useChoferes();
  const [driverData, setDriverData] = useState<ChoferModelFromBack | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  const vehicleData: any = driverData?.general?.vehiculo || {};

  useEffect(() => {
    const fetchDriver = async () => {
      try {
        const updatedDriver = await ChoferService.getDriver(driver.id);
        setDriverData(updatedDriver);
      } catch (error) {
        console.error('Error al obtener datos del chofer:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver.id]);

  if (loading) {
    return (
      <div className="w-full bg-page-light p-10 text-center dark:bg-page-dark">
        <p className="text-inkMuted dark:text-inkMuted-dark">
          Cargando información del chofer...
        </p>
      </div>
    );
  }

  if (!driverData) {
    return (
      <div className="w-full bg-page-light p-10 text-center dark:bg-page-dark">
        <p className="text-red-500 dark:text-danger-dark">
          No se encontró información del chofer.
        </p>
      </div>
    );
  }

  const functionalities = (state: ChoferEstado) => {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          disabled={isOpened}
          variant="light"
          color="blue"
          onClick={() => forceBreak(driver.id)}
          className="whitespace-nowrap"
        >
          Iniciar break
        </Button>
        <Button
          disabled={isOpened}
          variant="light"
          color="red"
          onClick={() => closeShift(driver.id)}
          className="whitespace-nowrap"
        >
          Terminar jornada
        </Button>
        {state === ChoferEstado.EXCEEDED && (
          <Button
            disabled={isOpened}
            variant="light"
            color="blue"
            onClick={() => extendExcess(driver)}
            className="whitespace-nowrap w-auto"
          >
            Extender horario
          </Button>
        )}
        <Button
          variant="light"
          color="green"
          onClick={() =>
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${driver.current_latitude},${driver.current_longitude}`
            )
          }
          className="whitespace-nowrap"
        >
          <div className="flex items-center">
            <FontAwesomeIcon icon={faMap} className="mr-2" />
            Maps
          </div>
        </Button>
      </div>
    );
  };

  return (
    <div className="w-full bg-page-light p-6 dark:bg-page-dark">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Datos del chofer */}
        <div className="relative rounded-2xl border  bg-general-card-light p-6 border-white/20 dark:bg-general-card-dark md:col-span-1">
          <FontAwesomeIcon
            icon={faMedal}
            className="absolute top-6 right-6"
            size="lg"
            color={mapDriverCategoryToIconColor(driver.category)}
          />
          <p className={labelClass}>Chofer</p>
          <h1 className="text-2xl font-bold text-ink dark:text-ink-dark">
            <span className="mr-2 text-secondary-500">#{driver.code}</span>
            {driver.name}
          </h1>
          <span
            className={`mt-3 inline-block rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs font-semibold dark:bg-white/10 ${driverStateTextClass(
              driver.state
            )}`}
          >
            {driver.state}
          </span>

          <div className="mt-5 flex flex-col gap-4">
            <InfoField label="Teléfono">
              <span className="font-semibold text-blue-600 dark:text-info-dark">
                {driverData.personal.telefonoEmergencia}
              </span>
            </InfoField>
            <InfoField label="Turno pactado">
              <span className={moneyClass}>
                de{' '}
                {moment()
                  .hour(driver.shift_init_hour)
                  .minute(driver.shift_init_minutes || 0)
                  .format('HH:mm')}{' '}
                a{' '}
                {moment()
                  .hour(driver.shift_end_hour)
                  .minute(driver.shift_end_minutes || 0)
                  .format('HH:mm')}{' '}
                hs.
              </span>
            </InfoField>
          </div>

          <div className="mt-6 border-t border-secondary-100 pt-4 dark:border-white/10">
            {functionalities(driver.state)}
          </div>
        </div>

        {/* Columna derecha */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:col-span-2">
          {/* Vehículo */}
          <div className={cardClass}>
            <CardHeader icon={<Car size={18} />} title="Vehículo" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <InfoField label="Marca">
                <span className="font-semibold text-blue-600 dark:text-info-dark">
                  {vehicleData?.modelo?.fabricante.nombre}
                </span>
              </InfoField>
              <InfoField label="Modelo">
                <span className="font-semibold text-blue-600 dark:text-info-dark">
                  {vehicleData?.modelo?.nombre}
                </span>
              </InfoField>
              <InfoField label="Patente">
                <span className="inline-block rounded-md border border-blue-300 bg-gray-100 px-3 py-0.5 text-sm font-semibold uppercase tracking-widest text-blue-600 shadow-sm dark:border-white/20 dark:bg-white/10 dark:text-info-dark">
                  {vehicleData?.patente}
                </span>
              </InfoField>
              <InfoField label="Categoría">
                <span
                  className={`font-semibold ${vehicleTypeBadgeClass(
                    driver?.vehicle_type
                  )}`}
                >
                  {vehicleTypeLabel(driver?.vehicle_type)}
                </span>
              </InfoField>
              {driver.vehicle_fuel && (
                <InfoField label="Combustible">
                  <span className="font-semibold text-blue-600 dark:text-info-dark">
                    {driver.vehicle_fuel}
                  </span>
                </InfoField>
              )}
            </div>
          </div>

          {/* Turno */}
          <div className={cardClass}>
            <CardHeader icon={<Clock size={18} />} title="Turno" />
            <div className="flex flex-col gap-3">
              <InfoField label="Hora de inicio de turno">
                <span className={moneyClass}>
                  {driver.open_shift_date_time?.format('HH:mm') ?? '--:--'} hs.
                </span>
              </InfoField>
              <InfoField label="Tiempo trabajado">
                <span className={moneyClass}>
                  {moment()
                    .hours(0)
                    .minutes(0)
                    .add(driver.worked_minutes, 'minutes')
                    .format('HH:mm')}{' '}
                  hs.
                </span>
              </InfoField>
              <InfoField label="Recaudación total hoy">
                <span className={moneyClass}>
                  ${Number(driver.income_since_work_day_started).toFixed(2)}
                </span>
              </InfoField>
              <InfoField label="Recaudación x hora hoy">
                <span className={moneyClass}>
                  ${Number(driver.income_for_worked_hours).toFixed(2)}
                </span>
              </InfoField>
              {driver.state === ChoferEstado.ON_BREAK &&
                driver.finished_break_date_time && (
                  <InfoField label="Break hasta">
                    <span
                      className={
                        moment().isBefore(driver.finished_break_date_time)
                          ? 'font-semibold text-green-500 dark:text-success-dark'
                          : 'font-semibold text-red-500 dark:text-danger-dark'
                      }
                    >
                      {driver.finished_break_date_time.format('HH:mm')}
                    </span>
                  </InfoField>
                )}
            </div>
          </div>

          {/* Promedios */}
          <div className={`${cardClass} sm:col-span-2`}>
            <CardHeader icon={<TrendingUp size={18} />} title="Promedios" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <InfoField label="Promedio quincenal x día">
                <span className={moneyClass}>
                  ${Number(driver.prom_income_current_month).toFixed(2)}
                </span>
              </InfoField>
              <InfoField label="Promedio quincenal x hora">
                <span className={moneyClass}>
                  ${Number(driver.prom_income_per_hour).toFixed(2)}
                </span>
              </InfoField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
